import 'react-fontpicker-ts/dist/index.css' // Overrides in App.js so order matters
import './App.css'
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom'
import '@fontsource/inter'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { NewCampaign } from './pages/NewCampaign'
import { VideoCampaign } from './pages/VideoCampaign'
import { CampaignConfig } from './pages/CampaignConfig'
import { CampaignDetails } from './pages/CampaignDetails'
import { TemplateLibrary } from './pages/TemplateLibrary'
import { TemplateBuilder } from './pages/TemplateBuilder'
import { CampaignAssetEditor } from './pages/CampaignAssetEditor'
import { LibraryTemplateEditor } from './pages/LibraryTemplateEditor'
import { LibraryTemplatePreview } from './pages/LibraryTemplatePreview'
import { Campaigns } from './pages/Campaigns'
import { BlogCampaign } from './pages/BlogCampaign'
import { ListingCampaign } from './pages/ListingCampaign'
import { CalendarPage } from './pages/CalendarPage'
import { Pricing } from './pages/Pricing'
import { Onboarding } from './pages/Onboarding'
import { Settings } from './pages/Settings'
import { SignupPage } from './pages/SignupPage'
import { PostSignup } from './pages/PostSignup'
import { Login } from './pages/Login'
import { Test } from './test'
import config from './api/config'
import { fabric } from 'fabric'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ClerkProvider } from '@clerk/clerk-react'
import { PostHogProvider } from 'posthog-js/react'
import { ProtectedLayout } from './layouts/ProtectedLayout'
import { HelmetProvider } from 'react-helmet-async'
import { Seo } from './SEO'

const router = createBrowserRouter([
    {
        path: '/',
        element: <Seo />,
        children: [
            {
                path: 'merchant/',
                element: <ProtectedLayout />,
                children: [
                    {
                        path: 'create_blog_campaign_v2',
                        element: <BlogCampaign />,
                    },
                    {
                        path: 'create_ai_quotes_campaign_v2',
                        element: <CampaignConfig />,
                    },
                    {
                        path: 'campaign_builder',
                        element: <NewCampaign />,
                    },
                    {
                        path: 'create_quotes_v2',
                        element: <NewCampaign />,
                    },
                    {
                        path: 'create_video_campaign',
                        element: <VideoCampaign />,
                    },
                    {
                        path: 'calendar_v2',
                        element: <CalendarPage />,
                    },
                    {
                        path: 'template_library',
                        element: <TemplateLibrary />,
                    },
                    {
                        path: 'builder/:templateType',
                        element: <TemplateBuilder />,
                    },
                    {
                        path: 'template_library/:templateId/edit',
                        element: <LibraryTemplateEditor />,
                    },
                    {
                        path: 'template_library/:templateId/preview',
                        element: <LibraryTemplatePreview />,
                    },
                    {
                        path: 'view_campaigns',
                        element: <Campaigns />,
                    },
                    {
                        path: 'view_campaign_v2/:campaignId',
                        element: <CampaignDetails />,
                    },
                    {
                        path: 'campaigns/:campaignId/assets/:assetId/edit',
                        element: <CampaignAssetEditor />,
                    },
                    {
                        path: 'pricing',
                        element: <Pricing />,
                    },
                    {
                        path: 'settings',
                        element: <Settings />,
                    },
                    {
                        path: 'create_listing_campaign',
                        element: <ListingCampaign />,
                    },
                ],
            },
            {
                path: 'merchant/onboarding',
                element: <ProtectedLayout isDashboardLayout={false} />,
                children: [
                    {
                        index: true,
                        element: <Onboarding />,
                    },
                ],
            },
            {
                path: 'test',
                element: <Test />,
            },
            {
                path: 'auth/signup',
                element: <SignupPage />,
            },
            {
                path: 'auth/post-signup',
                element: <PostSignup />,
            },
            {
                path: 'auth/login',
                element: <Login />,
            },
        ],
    },
])

const PUBLISHABLE_KEY = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY
const setupFabric = () => {
    // Hack: This is necessary to serialize/deserialize IDs for objects in
    // a canvas. Find a better way to do this...
    const originalToObject = fabric.Object.prototype.toObject
    const extraProps = ['id', 'name']
    fabric.Object.prototype.toObject = function (additionalProperties) {
        return originalToObject.call(
            this,
            extraProps.concat(additionalProperties)
        )
    }
}

const _setup = () => {
    setupFabric()
}

_setup()

const queryClient = new QueryClient()
const authProvider = (
    <ClerkProvider
        publishableKey={PUBLISHABLE_KEY}
        appearance={{
            elements: {
                cardBox: {
                    border: 'none',
                    boxShadow: 'none',
                },
            },
            variables: {
                colorPrimary: '#727cf5',
            },
        }}
        signInFallbackRedirectUrl="/merchant/calendar_v2"
        signUpFallbackRedirectUrl="/auth/post-signup"
        afterSignOutUrl="/auth/login"
    >
        <RouterProvider router={router} />
    </ClerkProvider>
)

const appChildren = (
    <div className="App">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <QueryClientProvider client={queryClient}>
                <HelmetProvider>{authProvider}</HelmetProvider>
            </QueryClientProvider>
        </LocalizationProvider>
    </div>
)

const isProd = process.env.NODE_ENV === 'production'
const postHogOptions = {
    api_host: process.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    defaults: '2025-05-24',
}
function App() {
    return isProd ? (
        <PostHogProvider
            apiKey={process.env.REACT_APP_PUBLIC_POSTHOG_KEY}
            options={postHogOptions}
        >
            {appChildren}
        </PostHogProvider>
    ) : (
        <>{appChildren}</>
    )
}

export default App
